import React from 'react';
import { ThemeProvider, CssBaseline, Box, Container, Typography, Link } from '@mui/material';
import { darkTheme, lightTheme, AppThemeMode } from '../theme';
import Navbar from '../components/Navbar';

export type PolicyVariant = 'privacy' | 'tos';

export interface PrivacyPolicyTOSProps {
  variant: PolicyVariant;
}

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="h4" sx={{ mt: 4, mb: 1.5 }}>
    {children}
  </Typography>
);

const Paragraph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body1" sx={{ mb: 2 }}>
    {children}
  </Typography>
);

const List: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="ul" sx={{ pl: 3, mb: 2, mt: 0 }}>
    {children}
  </Box>
);

const ListItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="li" sx={{ mb: 1.25 }}>
    <Typography variant="body1" component="span">
      {children}
    </Typography>
  </Box>
);

const PrivacyContent: React.FC = () => (
  <>
    <Typography variant="h3" sx={{ mb: 2 }}>Privacy Policy</Typography>
    <Paragraph>
      <strong>Effective Date:</strong> September 7, 2025
    </Paragraph>
    <Paragraph>
      <strong>Last Updated:</strong> September 7, 2025
    </Paragraph>
    <Paragraph>
      MCTraps (
      <Typography component="span" variant="body1" sx={{ fontStyle: 'italic' }}>
        “we,” “our,” or “us”
      </Typography>
      ) values your privacy. This Privacy Policy explains how we collect,
      use, and protect your information when you use our website or application.
    </Paragraph>

    <SectionHeading>1. Information We Collect</SectionHeading>
    <Paragraph>
      When you use our service, we may collect the following information:
    </Paragraph>
    <List>
      <ListItem>
        <strong>Account Information</strong>: When you sign up using a provider with an
        email (e.g., Google, Discord, Email), we store your <strong>email address</strong>,
        <strong> username</strong>, and <strong>profile picture</strong> provided by that service.
      </ListItem>
      <ListItem>
        <strong>Usage Data</strong>: Through Firebase Analytics and related services, we may
        collect anonymous information about how you use the app, such as pages visited,
        time spent, and general device details.
      </ListItem>
      <ListItem>
        <strong>Donation Information</strong>: When you make a donation through Ko-fi, your
        payment information is processed by Ko-fi and Stripe. We do not receive or store
        your full payment details.
      </ListItem>
    </List>

    <SectionHeading>2. How We Use Your Information</SectionHeading>
    <Paragraph>We use your information to:</Paragraph>
    <List>
      <ListItem>Create and manage your account.</ListItem>
      <ListItem>Allow you to log in using your chosen provider.</ListItem>
      <ListItem>Display your username and profile picture in the app.</ListItem>
      <ListItem>Improve our services through analytics and debugging.</ListItem>
      <ListItem>Process donations through Ko-fi (via their third-party payment providers).</ListItem>
      <ListItem>Communicate with you regarding important updates, security notices, or support.</ListItem>
    </List>

    <SectionHeading>3. How We Store and Protect Data</SectionHeading>
    <List>
      <ListItem>
        All account information (email, username, profile picture) is securely stored in
        <strong> Google Firebase Authentication, Cloud Firestore Database, and Firebase Storage.</strong>
      </ListItem>
      <ListItem>
        Firebase automatically encrypts data <strong>in transit and at rest</strong>.
      </ListItem>
      <ListItem>
        Donation transaction details are securely processed by <strong>Ko-fi</strong>; we do not store your
        payment card or banking details.
      </ListItem>
    </List>

    <SectionHeading>4. Sharing of Information</SectionHeading>
    <List>
      <ListItem>We do <strong>not sell, rent, or trade</strong> your personal information to third parties.</ListItem>
      <ListItem>
        Your data is shared only with:
        <List>
          <ListItem>Firebase (for hosting, authentication, and analytics).</ListItem>
          <ListItem>Ko-fi and its payment processors (only if you choose to donate).</ListItem>
        </List>
      </ListItem>
    </List>

    <SectionHeading>5. Data Retention and Deletion</SectionHeading>
    <List>
      <ListItem>We retain your information for as long as your account is active.</ListItem>
      <ListItem>Donation records may be kept for legal, tax, and accounting purposes.</ListItem>
      <ListItem>
        You may request deletion of your account and personal data at any time by contacting us at
        <strong> support@mctraps.com</strong>.
      </ListItem>
    </List>

    <SectionHeading>6. Children’s Privacy</SectionHeading>
    <Paragraph>
      Our services are not directed toward children under 13 (or under the age required by
      local law). We do not knowingly collect information from children.
    </Paragraph>

    <SectionHeading>7. Your Privacy Rights</SectionHeading>
    <Paragraph>Depending on your location, you may have rights to:</Paragraph>
    <List>
      <ListItem>Access the personal data we hold about you.</ListItem>
      <ListItem>Request correction or deletion of your data.</ListItem>
      <ListItem>Object to or limit processing of your data.</ListItem>
    </List>
    <Paragraph>
      To exercise these rights, please contact us at <strong>support@mctraps.com</strong>.
    </Paragraph>

    <SectionHeading>8. Changes to This Policy</SectionHeading>
    <Paragraph>
      We may update this Privacy Policy from time to time. Updates will be posted here with a new
      “Last Updated” date.
    </Paragraph>

    <SectionHeading>9. Contact Us</SectionHeading>
    <Paragraph>
      If you have any questions about this Privacy Policy, please contact us at:
    </Paragraph>
    <Paragraph>
      📧 <strong>support@mctraps.com</strong>
    </Paragraph>
  </>
);

const TermsContent: React.FC = () => (
  <>
    <Typography variant="h3" sx={{ mb: 2 }}>Terms of Service</Typography>
    <Paragraph>
      <strong>Effective Date:</strong> September 7, 2025
    </Paragraph>
    <Paragraph>
      <strong>Last Updated:</strong> September 7, 2025
    </Paragraph>
    <Paragraph>
      Welcome to MCTraps (
      <Typography component="span" variant="body1" sx={{ fontStyle: 'italic' }}>
        “we,” “our,” or “us”
      </Typography>
      ). By accessing or using our services, you agree to these Terms of Service. Please read them carefully.
    </Paragraph>

    <SectionHeading>1. Acceptance of Terms</SectionHeading>
    <Paragraph>
      By creating an account, accessing, donating, or using our app/website, you agree to comply with and be bound by these Terms.
    </Paragraph>

    <SectionHeading>2. Eligibility</SectionHeading>
    <List>
      <ListItem>You must be at least 13 years old (or the age required by local law) to use our services.</ListItem>
      <ListItem>By using our services, you confirm that you meet this requirement.</ListItem>
    </List>

    <SectionHeading>3. Account Registration</SectionHeading>
    <List>
      <ListItem>You may register using a provider with an email (e.g., Google, Discord, Email).</ListItem>
      <ListItem>
        We store your <strong>email address, username, and profile picture</strong> to create your account.
      </ListItem>
      <ListItem>
        You are responsible for keeping your login credentials secure and for all activity under your account.
      </ListItem>
    </List>

    <SectionHeading>4. Donations</SectionHeading>
    <List>
      <ListItem>We accept <strong>voluntary donations</strong> through <strong>Ko-fi</strong> to support our services.</ListItem>
      <ListItem>All payments are processed securely by Ko-fi and Stripe</ListItem>
      <ListItem>We do <strong>not</strong> store your payment card or banking details.</ListItem>
      <ListItem>Donations are <strong>non-refundable</strong>, except where required by law.</ListItem>
      <ListItem>
        Making a donation does not entitle you to special privileges or services unless explicitly stated.
      </ListItem>
    </List>

    <SectionHeading>5. Acceptable Use</SectionHeading>
    <Paragraph>You agree not to:</Paragraph>
    <List>
      <ListItem>Use our services for unlawful purposes.</ListItem>
      <ListItem>Attempt to access or tamper with accounts, servers, or networks without authorization.</ListItem>
      <ListItem>Upload harmful or malicious content.</ListItem>
      <ListItem>Impersonate others or misrepresent your identity.</ListItem>
    </List>

    <SectionHeading>6. Intellectual Property</SectionHeading>
    <List>
      <ListItem>
        All content, features, and functionality provided through our services are owned by MCTraps or its licensors.
      </ListItem>
      <ListItem>
        You may not copy, distribute, or modify our content without written permission.
      </ListItem>
    </List>

    <SectionHeading>7. Termination</SectionHeading>
    <Paragraph>
      We may suspend or terminate your account if you violate these Terms or engage in harmful behavior. Upon termination, your right to access our services will stop immediately.
    </Paragraph>

    <SectionHeading>8. Disclaimer of Warranties</SectionHeading>
    <Paragraph>
      Our services are provided “as is” and “as available.” We make no guarantees about availability, reliability, or suitability for your particular purpose.
    </Paragraph>

    <SectionHeading>9. Limitation of Liability</SectionHeading>
    <Paragraph>
      To the maximum extent permitted by law, MCTraps is not liable for any damages resulting from your use or inability to use our services, including but not limited to failed or delayed donations.
    </Paragraph>

    <SectionHeading>10. Changes to Terms</SectionHeading>
    <Paragraph>
      We may update these Terms from time to time. If we make changes, we will post the updated Terms here with a new “Last Updated” date.
    </Paragraph>

    <SectionHeading>11. Contact Us</SectionHeading>
    <Paragraph>
      If you have questions about these Terms, please contact us at:
    </Paragraph>
    <Paragraph>
      📧 <strong>support@mctraps.com</strong>
    </Paragraph>
  </>
);

const PrivacyPolicyTOS: React.FC<PrivacyPolicyTOSProps> = ({ variant }) => {
  const [mode, setMode] = React.useState<AppThemeMode>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem('themeMode') as AppThemeMode | null;
        if (stored === 'light' || stored === 'dark') return stored;
      } catch {}
    }
    return 'dark';
  });

  const currentTheme = mode === 'light' ? lightTheme : darkTheme;
  const toggleMode = React.useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined') window.localStorage.setItem('themeMode', mode);
    } catch {}
  }, [mode]);

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Navbar mode={mode} onToggleTheme={toggleMode} />
      <Box sx={{ height: 96 }} />
      <Container maxWidth="md" sx={{ pb: 8 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            <Link component="a" className="link" href="/home" underline="none">
              ← Back to Home
            </Link>
          </Typography>
        </Box>

        {variant === 'privacy' ? <PrivacyContent /> : <TermsContent />}
      </Container>
    </ThemeProvider>
  );
};

export default PrivacyPolicyTOS;


